import type { UserProfileView } from "@growthwise/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { isAuthConfigured, loadAuthProvider } from "../adapters/registry";
import type { AuthSession } from "../adapters/ports";
import { isPortalPath } from "../i18n";
import { ApiError, api } from "../services/api-client";

export type AuthStatus = "loading" | "signed-out" | "signed-in";

interface AuthContextValue {
  status: AuthStatus;
  session: AuthSession | null;
  /** Our own user record, fetched from the API after sign-in. */
  profile: UserProfileView | null;
  /** True only when both the token claim and the stored record say admin. */
  isAdmin: boolean;
  emailVerified: boolean;
  /** Set when a request failed with 401 and the user was signed out. */
  sessionExpired: boolean;
  providerConfigured: boolean;
  refresh: () => Promise<void>;
  reloadProfile: () => Promise<void>;
  signOut: () => Promise<void>;
  clearSessionExpired: () => void;
  setProfile: (profile: UserProfileView) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  /**
   * Whether a provider exists at all, answered without loading its SDK.
   *
   * The adapter itself is imported lazily (see `loadAuthProvider`), so a
   * visitor who only reads the public pages never downloads the Firebase
   * Auth bundle on the critical path. The session is attached immediately
   * on portal routes, and after idle time on marketing pages so a returning
   * visitor still sees "My account" in the header.
   */
  const configured = useMemo(() => isAuthConfigured(), []);
  const location = useLocation();

  const [status, setStatus] = useState<AuthStatus>(
    configured ? "loading" : "signed-out",
  );
  const [session, setSession] = useState<AuthSession | null>(null);
  const [profile, setProfileState] = useState<UserProfileView | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Guards against a slow profile fetch from a previous session overwriting
  // the current one after a fast sign-out/sign-in.
  const loadIdRef = useRef(0);
  const sessionAttachedRef = useRef(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const loadProfile = useCallback(async (): Promise<void> => {
    const loadId = loadIdRef.current + 1;
    loadIdRef.current = loadId;

    try {
      const { user } = await api.getProfile();
      if (loadIdRef.current === loadId) setProfileState(user);
    } catch (error: unknown) {
      if (loadIdRef.current !== loadId) return;

      if (error instanceof ApiError && error.isSessionProblem) {
        setSessionExpired(true);
        await loadAuthProvider()
          .then((provider) => provider.signOut())
          .catch(() => undefined);
        setSession(null);
        setProfileState(null);
        setStatus("signed-out");
        return;
      }
      // A transient failure leaves the user signed in; the screens that need
      // the profile show their own retry rather than ejecting the session.
      setProfileState(null);
    }
  }, []);

  const attachSession = useCallback((): void => {
    if (!configured || sessionAttachedRef.current) return;
    sessionAttachedRef.current = true;

    void loadAuthProvider()
      .then((provider) => {
        unsubscribeRef.current = provider.subscribe((next) => {
          setSession(next);
          if (next) {
            setStatus("signed-in");
            setSessionExpired(false);
            void loadProfile();
          } else {
            setStatus("signed-out");
            setProfileState(null);
            loadIdRef.current += 1;
          }
        });
      })
      .catch(() => {
        sessionAttachedRef.current = false;
        setStatus("signed-out");
      });
  }, [configured, loadProfile]);

  useEffect(() => {
    if (!configured) {
      setStatus("signed-out");
      return undefined;
    }

    if (isPortalPath(location.pathname)) {
      attachSession();
      return undefined;
    }

    const idleWindow = window as Window & {
      requestIdleCallback?: (
        callback: () => void,
        options?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (typeof idleWindow.requestIdleCallback === "function") {
      idleId = idleWindow.requestIdleCallback(attachSession, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(attachSession, 1500);
    }

    return () => {
      if (idleId !== undefined) idleWindow.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [attachSession, configured, location.pathname]);

  useEffect(
    () => () => {
      unsubscribeRef.current?.();
    },
    [],
  );

  const refresh = useCallback(async () => {
    const provider = await loadAuthProvider();
    const next = await provider.refreshSession();
    setSession(next);
    if (next) await loadProfile();
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    const provider = await loadAuthProvider();
    await provider.signOut();
    setSession(null);
    setProfileState(null);
    setStatus("signed-out");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      profile,
      // Both signals must agree. The token claim alone could be stale, and
      // the stored record alone could outlive a revoked session -- and in
      // any case the server re-checks this on every admin request, so this
      // flag only decides what the UI offers, never what is permitted.
      isAdmin: session?.claimedRole === "admin" && profile?.role === "admin",
      emailVerified: Boolean(session?.emailVerified),
      sessionExpired,
      providerConfigured: configured,
      refresh,
      reloadProfile: loadProfile,
      signOut,
      clearSessionExpired: () => setSessionExpired(false),
      setProfile: setProfileState,
    }),
    [
      configured,
      loadProfile,
      profile,
      refresh,
      session,
      sessionExpired,
      signOut,
      status,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider");
  return context;
};
